import * as THREE from 'three/webgpu';
import {createSketchEntityPersistentId} from '../identity/SketchPersistentIdFactory';
import type {
    SketchCircularPatternDefinitionSnapshot,
    SketchCircularPatternInstanceRecord,
    SketchCircularPatternParameters,
} from '../../../types/sketch/circular-pattern/SketchCircularPatternTypes';

/**
 * 圆周草图阵列定义对象。
 *
 * 职责：
 * 1. 在草图对象树中持有圆周阵列的参数化定义；
 * 2. 保存源实体与各实例的稳定持久身份映射；
 * 3. 为历史快照和离线持久化提供纯数据快照。
 *
 * 本对象不承载可渲染几何。阵列实例仍是普通草图实体，保证选择、约束和下游引用
 * 继续沿用统一草图实体链路。
 */
export class SketchCircularPatternObject extends THREE.Object3D {
    /** 跨模块识别阵列定义对象的稳定标记。 */
    public readonly isSketchCircularPatternObject = true;

    /** 草图持久 ID。 */
    public persistentId: string = createSketchEntityPersistentId();

    /** 源草图对象持久 ID。 */
    private sourceEntityPersistentIds: string[];

    /** 当前阵列参数。 */
    private parameters: SketchCircularPatternParameters;

    /** 实例稳定身份记录。 */
    private instances: SketchCircularPatternInstanceRecord[];

    /**
     * 创建圆周草图阵列定义对象。
     *
     * @param input 初始定义快照。
     */
    public constructor(input: Omit<SketchCircularPatternDefinitionSnapshot, 'persistentId'>) {
        super();
        this.name = '圆周草图阵列';
        this.sourceEntityPersistentIds = [...input.sourceEntityPersistentIds];
        this.parameters = this.cloneParameters(input.parameters);
        this.instances = this.cloneInstances(input.instances);
    }

    /**
     * 获取不可变定义快照。
     *
     * @returns 与运行时数组解耦的定义快照。
     */
    public getDefinitionSnapshot(): SketchCircularPatternDefinitionSnapshot {
        return {
            persistentId: this.persistentId,
            sourceEntityPersistentIds: [...this.sourceEntityPersistentIds],
            parameters: this.cloneParameters(this.parameters),
            instances: this.cloneInstances(this.instances),
        };
    }

    /**
     * 原位更新阵列定义。
     *
     * @param input 新的定义内容。
     */
    public updateDefinition(input: Omit<SketchCircularPatternDefinitionSnapshot, 'persistentId'>): void {
        this.sourceEntityPersistentIds = [...input.sourceEntityPersistentIds];
        this.parameters = this.cloneParameters(input.parameters);
        this.instances = this.cloneInstances(input.instances);
    }

    /**
     * 为复制出的新逻辑草图换新阵列身份并重映射全部实体引用。
     *
     * 该方法只能在所属草图实体已经完成持久 ID 换新后调用。阵列定义属于草图主状态，
     * 源实体或任一实例引用缺失映射都表示复制域不完整，必须直接失败，禁止生成悬空阵列。
     *
     * @param persistentIdMap 当前草图复制事务中的源 ID 到目标 ID 映射。
     */
    public regeneratePersistentIdentity(persistentIdMap: Map<string, string>): void {
        const sourcePatternPersistentId = this.persistentId;
        const remapEntityPersistentId = (persistentId: string): string => {
            const remappedPersistentId = persistentIdMap.get(persistentId);
            if (!remappedPersistentId) {
                throw new Error(`圆周草图阵列复制失败：未找到实体持久 ID 映射 ${persistentId}。`);
            }
            return remappedPersistentId;
        };

        // 阵列定义中的源实体与实例记录必须和复制后的真实实体保持同一身份域。
        this.sourceEntityPersistentIds = this.sourceEntityPersistentIds.map(remapEntityPersistentId);
        this.instances = this.instances.map((instance) => ({
            instanceIndex: instance.instanceIndex,
            entityPersistentIds: instance.entityPersistentIds.map(remapEntityPersistentId),
        }));

        // 阵列自身也属于新逻辑草图，必须换新身份并纳入同一事务映射。
        this.persistentId = createSketchEntityPersistentId();
        persistentIdMap.set(sourcePatternPersistentId, this.persistentId);
    }

    /**
     * 克隆阵列定义对象。
     *
     * 历史快照表示同一逻辑阵列，因此克隆对象继承原持久 ID；复制成新逻辑阵列时，
     * 调用方必须显式分配新的持久 ID。
     *
     * @returns 与当前对象定义解耦的阵列对象副本。
     */
    public override clone(): this {
        const snapshot = this.getDefinitionSnapshot();
        const cloned = new SketchCircularPatternObject({
            sourceEntityPersistentIds: snapshot.sourceEntityPersistentIds,
            parameters: snapshot.parameters,
            instances: snapshot.instances,
        });
        cloned.persistentId = this.persistentId;
        cloned.name = this.name;
        cloned.visible = this.visible;
        return cloned as this;
    }

    /**
     * 复制阵列参数。
     *
     * @param parameters 源参数。
     * @returns 深复制后的参数。
     */
    private cloneParameters(parameters: SketchCircularPatternParameters): SketchCircularPatternParameters {
        return {
            ...parameters,
            center2d: [parameters.center2d[0], parameters.center2d[1]],
            skippedInstanceIndexes: [...parameters.skippedInstanceIndexes],
        };
    }

    /**
     * 复制实例身份记录。
     *
     * @param instances 源实例记录。
     * @returns 深复制后的实例记录。
     */
    private cloneInstances(instances: readonly SketchCircularPatternInstanceRecord[]): SketchCircularPatternInstanceRecord[] {
        return instances.map((instance) => ({
            instanceIndex: instance.instanceIndex,
            entityPersistentIds: [...instance.entityPersistentIds],
        }));
    }
}
